import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { TimePicker } from "../components/ui/time-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Plus,
  Play,
  Pause,
  Edit,
  Trash2,
  Activity,
  Clock,
  Mail,
  Calendar,
  DollarSign,
  Users,
  Settings,
  Zap,
  BarChart3
} from "lucide-react";
import { automationService } from "../services/automationService";
import { emailTemplateService } from "../services/emailTemplateService";
import { projectStatusService } from "../services/projectStatusService";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from "../hooks/use-toast";

export default function Automations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isStatsDialogOpen, setIsStatsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [deletingAutomation, setDeletingAutomation] = useState(null);
  const [newAutomation, setNewAutomation] = useState({
    name: "",
    description: "",
    trigger: {
      trigger_type: "time_based",
      schedule_frequency: "daily",
      schedule_time: "09:00",
      schedule_days: [],
      appointment_type: "initial_appointment",
      project_status: "",
      payment_due_days: "3",
      business_hours_only: false
    },
    action: {
      action_type: "send_email",
      recipient_type: "fixed_email",
      fixed_recipients: [],
      email_template_id: "custom",
      email_subject: "",
      email_body: "",
      include_data_types: ["schedule"]
    },
    is_active: true
  });

  // API hooks
  const { data: automationsData, isLoading: loading, refetch } = useQuery({
    queryKey: ['automations'],
    queryFn: () => automationService.getAll()
  });

  const { data: statsData } = useQuery({
    queryKey: ['automation-stats'],
    queryFn: () => automationService.getStats()
  });

  const { data: emailTemplatesData } = useQuery({
    queryKey: ['email-templates', { active_only: true }],
    queryFn: () => emailTemplateService.getAll({ active_only: true })
  });

  const { data: projectStatusesData } = useQuery({
    queryKey: ['project-statuses'],
    queryFn: () => projectStatusService.getAll()
  });

  const { data: schedulerStatusData } = useQuery({
    queryKey: ['scheduler-status'],
    queryFn: () => automationService.getSchedulerStatus()
  });

  const createAutomationMutation = useMutation({
    mutationFn: (data) => automationService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] });
      toast({ description: "Automation created successfully!" });
      setIsDialogOpen(false);
      setNewAutomation({
        name: "",
        description: "",
        trigger: {
          trigger_type: "time_based",
          schedule_frequency: "daily",
          schedule_time: "09:00",
          schedule_days: [],
          appointment_type: "initial_appointment",
          project_status: "",
          payment_due_days: "3",
          business_hours_only: false
        },
        action: {
          action_type: "send_email",
          recipient_type: "fixed_email",
          fixed_recipients: [],
          email_template_id: "custom",
          email_subject: "",
          email_body: "",
          include_data_types: ["schedule"]
        },
        is_active: true
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to create automation"
      });
    }
  });

  const updateAutomationMutation = useMutation({
    mutationFn: ({ id, data }) => automationService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] });
      toast({ description: "Automation updated successfully!" });
      setIsEditDialogOpen(false);
      setEditingAutomation(null);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to update automation"
      });
    }
  });

  const deleteAutomationMutation = useMutation({
    mutationFn: (id) => automationService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] });
      toast({ description: "Automation deleted successfully!" });
      setIsDeleteDialogOpen(false);
      setDeletingAutomation(null);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to delete automation"
      });
    }
  });

  const toggleAutomationMutation = useMutation({
    mutationFn: ({ id, is_active }) => automationService.toggle(id, is_active),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] });
      toast({ description: "Automation status updated!" });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to toggle automation"
      });
    }
  });

  const executeAutomationMutation = useMutation({
    mutationFn: (id) => automationService.execute(id),
    onSuccess: () => {
      toast({ description: "Automation executed successfully!" });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to execute automation"
      });
    }
  });

  const automations = automationsData?.data || [];
  const stats = statsData?.data || {};
  const emailTemplates = emailTemplatesData?.data?.templates || [];
  const projectStatuses = projectStatusesData?.data || [];

  const triggerTypes = [
    { value: "time_based", label: "Time Based", icon: Clock },
    { value: "appointment_type", label: "Appointment Type", icon: Calendar },
    { value: "payment_due", label: "Payment Due", icon: DollarSign },
    { value: "project_status", label: "Project Status", icon: Settings }
  ];

  const appointmentTypes = [
    { value: "initial_appointment", label: "Initial Appointment" },
    { value: "design_consultation", label: "Design Consultation" },
    { value: "follow_up", label: "Follow Up" },
    { value: "zoom_follow_up", label: "Zoom Follow Up" },
    { value: "other", label: "Other" }
  ];

  const paymentDueDays = [
    { value: "0", label: "On Due Date" },
    { value: "1", label: "1 Day Before" },
    { value: "3", label: "3 Days Before" },
    { value: "7", label: "7 Days Before" },
    { value: "14", label: "14 Days Before" }
  ];

  const actionTypes = [
    { value: "send_email", label: "Send Email", icon: Mail },
    { value: "create_task", label: "Create Task", icon: Plus },
    { value: "update_record", label: "Update Record", icon: Edit }
  ];

  const recipientTypes = [
    { value: "fixed_email", label: "Fixed Email Address" },
    { value: "admin_email", label: "Admin Email" },
    { value: "customer_email", label: "Customer Email" },
    { value: "project_customer", label: "Project Customer" }
  ];

  const frequencies = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "weekdays", label: "Weekdays Only" },
    { value: "monthly", label: "Monthly" }
  ];

  const dataTypes = [
    { value: "schedule", label: "Schedule/Calendar" },
    { value: "projects", label: "Projects" },
    { value: "payments", label: "Payments" },
    { value: "customers", label: "Customers" }
  ];

  const handleCreateAutomation = () => {
    createAutomationMutation.mutate(newAutomation);
  };

  const handleEditAutomation = (automation) => {
    setEditingAutomation(automation);
    setIsEditDialogOpen(true);
  };

  const handleUpdateAutomation = () => {
    updateAutomationMutation.mutate({
      id: editingAutomation.id || editingAutomation._id,
      data: editingAutomation
    });
  };

  const handleDeleteAutomation = (automation) => {
    setDeletingAutomation(automation);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteAutomation = () => {
    deleteAutomationMutation.mutate(deletingAutomation.id || deletingAutomation._id);
  };

  const handleToggleAutomation = (automationId) => {
    const automation = automations.find(a => (a.id || a._id) === automationId);
    if (automation) {
      toggleAutomationMutation.mutate({
        id: automationId,
        is_active: !automation.is_active
      });
    }
  };

  const handleExecuteAutomation = (automationId) => {
    executeAutomationMutation.mutate(automationId);
  };

  const getTriggerIcon = (triggerType) => {
    const trigger = triggerTypes.find(t => t.value === triggerType);
    return trigger ? trigger.icon : Clock;
  };

  const getActionIcon = (actionType) => {
    const action = actionTypes.find(a => a.value === actionType);
    return action ? action.icon : Mail;
  };

  if (loading) {
    return <div className="p-6">Loading automations...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Automations</h1>
          <p className="text-gray-600 mt-1">Create and manage automated workflows for your business</p>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={() => setIsStatsDialogOpen(true)}
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Statistics
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-600 hover:bg-brand-700">
                <Plus className="mr-2 h-4 w-4" />
                New Automation
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Automation</DialogTitle>
                <DialogDescription>
                  Set up an automated workflow to streamline your business processes.
                </DialogDescription>
              </DialogHeader>
              
              {/* Create Automation Form */}
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Automation Name *</Label>
                    <Input
                      id="name"
                      value={newAutomation.name}
                      onChange={(e) => setNewAutomation({...newAutomation, name: e.target.value})}
                      placeholder="Daily Schedule Reminder"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={newAutomation.is_active}
                        onCheckedChange={(checked) => setNewAutomation({...newAutomation, is_active: checked})}
                      />
                      <span className="text-sm">{newAutomation.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newAutomation.description}
                    onChange={(e) => setNewAutomation({...newAutomation, description: e.target.value})}
                    placeholder="Describe what this automation does..."
                    rows={3}
                  />
                </div>

                {/* Trigger Configuration */}
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <Zap className="mr-2 h-4 w-4" />
                    Trigger Configuration
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Trigger Type</Label>
                      <Select 
                        value={newAutomation.trigger.trigger_type}
                        onValueChange={(value) => setNewAutomation({
                          ...newAutomation,
                          trigger: {...newAutomation.trigger, trigger_type: value}
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {triggerTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {newAutomation.trigger.trigger_type === "time_based" && (
                      <>
                        <div className="space-y-2">
                          <Label>Frequency</Label>
                          <Select 
                            value={newAutomation.trigger.schedule_frequency}
                            onValueChange={(value) => setNewAutomation({
                              ...newAutomation,
                              trigger: {...newAutomation.trigger, schedule_frequency: value}
                            })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {frequencies.map((freq) => (
                                <SelectItem key={freq.value} value={freq.value}>
                                  {freq.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Time</Label>
                          <TimePicker
                            value={newAutomation.trigger.schedule_time}
                            onChange={(value) => setNewAutomation({
                              ...newAutomation,
                              trigger: {...newAutomation.trigger, schedule_time: value}
                            })}
                            placeholder="Select time"
                          />
                        </div>
                      </>
                    )}

                    {newAutomation.trigger.trigger_type === "appointment_type" && (
                      <div className="space-y-2">
                        <Label>Appointment Type</Label>
                        <Select 
                          value={newAutomation.trigger.appointment_type}
                          onValueChange={(value) => setNewAutomation({
                            ...newAutomation,
                            trigger: {...newAutomation.trigger, appointment_type: value}
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {appointmentTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {newAutomation.trigger.trigger_type === "project_status" && (
                      <div className="space-y-2">
                        <Label>Project Status</Label>
                        <Select 
                          value={newAutomation.trigger.project_status}
                          onValueChange={(value) => setNewAutomation({
                            ...newAutomation,
                            trigger: {...newAutomation.trigger, project_status: value}
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select project status" />
                          </SelectTrigger>
                          <SelectContent>
                            {projectStatuses.map((status) => (
                              <SelectItem key={status.id || status._id} value={status.value}>
                                {status.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {newAutomation.trigger.trigger_type === "payment_due" && (
                      <div className="space-y-2">
                        <Label>Notify When</Label>
                        <Select 
                          value={newAutomation.trigger.payment_due_days}
                          onValueChange={(value) => setNewAutomation({
                            ...newAutomation,
                            trigger: {...newAutomation.trigger, payment_due_days: value}
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select notification timing" />
                          </SelectTrigger>
                          <SelectContent>
                            {paymentDueDays.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Configuration */}
                <div className="border rounded-lg p-4 bg-blue-50">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <Activity className="mr-2 h-4 w-4" />
                    Action Configuration
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Action Type</Label>
                        <Select 
                          value={newAutomation.action.action_type}
                          onValueChange={(value) => setNewAutomation({
                            ...newAutomation,
                            action: {...newAutomation.action, action_type: value}
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {actionTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {newAutomation.action.action_type === "send_email" && (
                        <div className="space-y-2">
                          <Label>Recipients</Label>
                          <Select 
                            value={newAutomation.action.recipient_type}
                            onValueChange={(value) => setNewAutomation({
                              ...newAutomation,
                              action: {...newAutomation.action, recipient_type: value}
                            })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {recipientTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    {newAutomation.action.action_type === "send_email" && (
                      <>
                        {newAutomation.action.recipient_type === "fixed_email" && (
                          <div className="space-y-2">
                            <Label>Email Addresses (comma separated)</Label>
                            <Input
                              value={newAutomation.action.fixed_recipients.join(', ')}
                              onChange={(e) => setNewAutomation({
                                ...newAutomation,
                                action: {
                                  ...newAutomation.action, 
                                  fixed_recipients: e.target.value.split(',').map(email => email.trim()).filter(email => email)
                                }
                              })}
                              placeholder="admin@reliablelandscapesdesign.com, manager@company.com"
                            />
                          </div>
                        )}
                        
                        <div className="space-y-2">
                          <Label>Email Template (Optional)</Label>
                          <Select 
                            value={newAutomation.action.email_template_id}
                            onValueChange={(value) => setNewAutomation({
                              ...newAutomation,
                              action: {...newAutomation.action, email_template_id: value}
                            })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select email template or use custom content" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="custom">Custom Content (No Template)</SelectItem>
                              {emailTemplates.map((template) => (
                                <SelectItem key={template.id || template._id} value={template.id || template._id}>
                                  {template.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {(!newAutomation.action.email_template_id || newAutomation.action.email_template_id === "custom") && (
                          <>
                            <div className="space-y-2">
                              <Label>Email Subject</Label>
                          <Input
                            value={newAutomation.action.email_subject}
                            onChange={(e) => setNewAutomation({
                              ...newAutomation,
                              action: {...newAutomation.action, email_subject: e.target.value}
                            })}
                            placeholder="Daily Schedule Report - LandscapeFlow"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Email Body (leave blank for auto-generated)</Label>
                          <Textarea
                            value={newAutomation.action.email_body}
                            onChange={(e) => setNewAutomation({
                              ...newAutomation,
                              action: {...newAutomation.action, email_body: e.target.value}
                            })}
                            placeholder="Custom email content..."
                            rows={4}
                          />
                        </div>
                      </>
                    )}

                        <div className="space-y-2">
                          <Label>Include Data Types</Label>
                          <div className="flex flex-wrap gap-2">
                            {dataTypes.map((dataType) => (
                              <Badge
                                key={dataType.value}
                                variant={newAutomation.action.include_data_types.includes(dataType.value) ? "default" : "outline"}
                                className="cursor-pointer"
                                onClick={() => {
                                  const currentTypes = newAutomation.action.include_data_types;
                                  const newTypes = currentTypes.includes(dataType.value)
                                    ? currentTypes.filter(t => t !== dataType.value)
                                    : [...currentTypes, dataType.value];
                                  
                                  setNewAutomation({
                                    ...newAutomation,
                                    action: {...newAutomation.action, include_data_types: newTypes}
                                  });
                                }}
                              >
                                {dataType.label}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateAutomation}
                  disabled={createAutomationMutation.isPending || !newAutomation.name}
                  className="bg-brand-600 hover:bg-brand-700"
                >
                  {createAutomationMutation.isPending ? "Creating..." : "Create Automation"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Automations</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_automations || 0}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Automations</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-brand-600">{stats.active_automations || 0}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Executions Today</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.executions_today || 0}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.success_rate || 0}%</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Scheduler Status</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-bold">
              {schedulerStatusData?.data?.running ? (
                <span className="text-green-600">Running ({schedulerStatusData.data.jobs} jobs)</span>
              ) : (
                <span className="text-red-600">Stopped</span>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Time-based: Hourly<br/>
              Task due check: Daily 12:01 AM PST
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Automations Table */}
      <Card>
        <CardHeader>
          <CardTitle>Your Automations</CardTitle>
        </CardHeader>
        <CardContent>
          {automations.length === 0 ? (
            <div className="text-center py-12">
              <Zap className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No automations yet</h3>
              <p className="text-gray-500 mb-6">Create your first automation to get started with workflow automation</p>
              <Button 
                onClick={() => setIsDialogOpen(true)}
                className="bg-brand-600 hover:bg-brand-700"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create First Automation
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Executed</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {automations.map((automation) => {
                  const TriggerIcon = getTriggerIcon(automation.trigger.trigger_type);
                  const ActionIcon = getActionIcon(automation.action.action_type);
                  
                  return (
                    <TableRow key={automation.id || automation._id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{automation.name}</div>
                          {automation.description && (
                            <div className="text-sm text-gray-500">{automation.description}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <TriggerIcon className="mr-2 h-4 w-4 text-gray-500" />
                          <span className="capitalize">{automation.trigger.trigger_type.replace('_', ' ')}</span>
                          {automation.trigger.trigger_type === "time_based" && automation.trigger.schedule_time && (
                            <Badge variant="outline" className="ml-2">
                              {automation.trigger.schedule_time}
                            </Badge>
                          )}
                          {automation.trigger.trigger_type === "appointment_type" && automation.trigger.appointment_type && (
                            <Badge variant="outline" className="ml-2">
                              {appointmentTypes.find(type => type.value === automation.trigger.appointment_type)?.label || automation.trigger.appointment_type}
                            </Badge>
                          )}
                          {automation.trigger.trigger_type === "project_status" && automation.trigger.project_status && (
                            <Badge variant="outline" className="ml-2">
                              {projectStatuses.find(status => status.value === automation.trigger.project_status)?.label || automation.trigger.project_status}
                            </Badge>
                          )}
                          {automation.trigger.trigger_type === "payment_due" && automation.trigger.payment_due_days && (
                            <Badge variant="outline" className="ml-2">
                              {paymentDueDays.find(option => option.value === automation.trigger.payment_due_days)?.label || `${automation.trigger.payment_due_days} days before`}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <ActionIcon className="mr-2 h-4 w-4 text-gray-500" />
                          <span className="capitalize">{automation.action.action_type.replace('_', ' ')}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={automation.is_active}
                            onCheckedChange={() => handleToggleAutomation(automation.id || automation._id)}
                            disabled={toggleAutomationMutation.isPending}
                          />
                          <Badge variant={automation.is_active ? "default" : "secondary"}>
                            {automation.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        {automation.last_executed ? (
                          <div className="text-sm">
                            {new Date(automation.last_executed).toLocaleDateString()}
                            <div className="text-gray-500">
                              {new Date(automation.last_executed).toLocaleTimeString()}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-500">Never</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleExecuteAutomation(automation.id || automation._id)}
                            disabled={executeAutomationMutation.isPending}
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditAutomation(automation)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteAutomation(automation)}
                            disabled={deleteAutomationMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Automation Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Automation</DialogTitle>
            <DialogDescription>
              Update your automation settings and configuration.
            </DialogDescription>
          </DialogHeader>
          
          {editingAutomation && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editName">Automation Name *</Label>
                  <Input
                    id="editName"
                    value={editingAutomation.name}
                    onChange={(e) => setEditingAutomation({...editingAutomation, name: e.target.value})}
                    placeholder="Daily Schedule Reminder"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={editingAutomation.is_active}
                      onCheckedChange={(checked) => setEditingAutomation({...editingAutomation, is_active: checked})}
                    />
                    <span className="text-sm">{editingAutomation.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="editDescription">Description</Label>
                <Textarea
                  id="editDescription"
                  value={editingAutomation.description || ""}
                  onChange={(e) => setEditingAutomation({...editingAutomation, description: e.target.value})}
                  placeholder="Describe what this automation does..."
                  rows={3}
                />
              </div>

              {/* Trigger Configuration */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <h3 className="font-semibold mb-3 flex items-center">
                  <Zap className="mr-2 h-4 w-4" />
                  Trigger Configuration
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Trigger Type</Label>
                    <Select 
                      value={editingAutomation.trigger.trigger_type}
                      onValueChange={(value) => setEditingAutomation({
                        ...editingAutomation,
                        trigger: {...editingAutomation.trigger, trigger_type: value}
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {triggerTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {editingAutomation.trigger.trigger_type === "time_based" && (
                    <>
                      <div className="space-y-2">
                        <Label>Frequency</Label>
                        <Select 
                          value={editingAutomation.trigger.schedule_frequency || "daily"}
                          onValueChange={(value) => setEditingAutomation({
                            ...editingAutomation,
                            trigger: {...editingAutomation.trigger, schedule_frequency: value}
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {frequencies.map((freq) => (
                              <SelectItem key={freq.value} value={freq.value}>
                                {freq.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Time</Label>
                        <TimePicker
                          value={editingAutomation.trigger.schedule_time || "09:00"}
                          onChange={(value) => setEditingAutomation({
                            ...editingAutomation,
                            trigger: {...editingAutomation.trigger, schedule_time: value}
                          })}
                          placeholder="Select time"
                        />
                      </div>
                    </>
                  )}

                  {editingAutomation.trigger.trigger_type === "appointment_type" && (
                    <div className="space-y-2">
                      <Label>Appointment Type</Label>
                      <Select 
                        value={editingAutomation.trigger.appointment_type || "initial_appointment"}
                        onValueChange={(value) => setEditingAutomation({
                          ...editingAutomation,
                          trigger: {...editingAutomation.trigger, appointment_type: value}
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {appointmentTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {editingAutomation.trigger.trigger_type === "project_status" && (
                    <div className="space-y-2">
                      <Label>Project Status</Label>
                      <Select 
                        value={editingAutomation.trigger.project_status || ""}
                        onValueChange={(value) => setEditingAutomation({
                          ...editingAutomation,
                          trigger: {...editingAutomation.trigger, project_status: value}
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select project status" />
                        </SelectTrigger>
                        <SelectContent>
                          {projectStatuses.map((status) => (
                            <SelectItem key={status.id || status._id} value={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {editingAutomation.trigger.trigger_type === "payment_due" && (
                    <div className="space-y-2">
                      <Label>Notify When</Label>
                      <Select 
                        value={editingAutomation.trigger.payment_due_days || "3"}
                        onValueChange={(value) => setEditingAutomation({
                          ...editingAutomation,
                          trigger: {...editingAutomation.trigger, payment_due_days: value}
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select notification timing" />
                        </SelectTrigger>
                        <SelectContent>
                          {paymentDueDays.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Configuration */}
              <div className="border rounded-lg p-4 bg-blue-50">
                <h3 className="font-semibold mb-3 flex items-center">
                  <Activity className="mr-2 h-4 w-4" />
                  Action Configuration
                </h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Action Type</Label>
                      <Select 
                        value={editingAutomation.action.action_type}
                        onValueChange={(value) => setEditingAutomation({
                          ...editingAutomation,
                          action: {...editingAutomation.action, action_type: value}
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {actionTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {editingAutomation.action.action_type === "send_email" && (
                      <div className="space-y-2">
                        <Label>Recipients</Label>
                        <Select 
                          value={editingAutomation.action.recipient_type}
                          onValueChange={(value) => setEditingAutomation({
                            ...editingAutomation,
                            action: {...editingAutomation.action, recipient_type: value}
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {recipientTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {editingAutomation.action.action_type === "send_email" && (
                    <>
                      {editingAutomation.action.recipient_type === "fixed_email" && (
                        <div className="space-y-2">
                          <Label>Email Addresses (comma separated)</Label>
                          <Input
                            value={editingAutomation.action.fixed_recipients?.join(', ') || ""}
                            onChange={(e) => setEditingAutomation({
                              ...editingAutomation,
                              action: {
                                ...editingAutomation.action, 
                                fixed_recipients: e.target.value.split(',').map(email => email.trim()).filter(email => email)
                              }
                            })}
                            placeholder="admin@reliablelandscapesdesign.com, manager@company.com"
                          />
                        </div>
                      )}
                      
                      <div className="space-y-2">
                        <Label>Email Template (Optional)</Label>
                        <Select 
                          value={editingAutomation.action.email_template_id || ""}
                          onValueChange={(value) => setEditingAutomation({
                            ...editingAutomation,
                            action: {...editingAutomation.action, email_template_id: value}
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select email template or use custom content" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="custom">Custom Content (No Template)</SelectItem>
                            {emailTemplates.map((template) => (
                              <SelectItem key={template.id || template._id} value={template.id || template._id}>
                                {template.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {(!editingAutomation.action.email_template_id || editingAutomation.action.email_template_id === "custom") && (
                        <>
                          <div className="space-y-2">
                            <Label>Email Subject</Label>
                        <Input
                          value={editingAutomation.action.email_subject || ""}
                          onChange={(e) => setEditingAutomation({
                            ...editingAutomation,
                            action: {...editingAutomation.action, email_subject: e.target.value}
                          })}
                          placeholder="Daily Schedule Report - LandscapeFlow"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Email Body (leave blank for auto-generated)</Label>
                        <Textarea
                          value={editingAutomation.action.email_body || ""}
                          onChange={(e) => setEditingAutomation({
                            ...editingAutomation,
                            action: {...editingAutomation.action, email_body: e.target.value}
                          })}
                          placeholder="Custom email content..."
                          rows={4}
                        />
                      </div>
                    </>
                  )}

                      <div className="space-y-2">
                        <Label>Include Data Types</Label>
                        <div className="flex flex-wrap gap-2">
                          {dataTypes.map((dataType) => (
                            <Badge
                              key={dataType.value}
                              variant={editingAutomation.action.include_data_types?.includes(dataType.value) ? "default" : "outline"}
                              className="cursor-pointer"
                              onClick={() => {
                                const currentTypes = editingAutomation.action.include_data_types || [];
                                const newTypes = currentTypes.includes(dataType.value)
                                  ? currentTypes.filter(t => t !== dataType.value)
                                  : [...currentTypes, dataType.value];
                                
                                setEditingAutomation({
                                  ...editingAutomation,
                                  action: {...editingAutomation.action, include_data_types: newTypes}
                                });
                              }}
                            >
                              {dataType.label}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateAutomation}
              disabled={updateAutomationMutation.isPending || !editingAutomation?.name}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {updateAutomationMutation.isPending ? "Updating..." : "Update Automation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Automation</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingAutomation?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={deleteAutomationMutation.isPending}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={confirmDeleteAutomation}
              disabled={deleteAutomationMutation.isPending}
            >
              {deleteAutomationMutation.isPending ? "Deleting..." : "Delete Automation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}